import React, { useContext, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
    AutoCompleteDescription,
    CloseDropdownText,
    AutoCompleteTemplateHeader,
    AutoListContainer,
    MultiDivRowView,
    CustomText,
    AddedCartButton,
    AddCartIcon,
    BoxView,
    Spinner
} from './SearchDropdown.styled';
import CustomLabel from 'src/pages/reportUsage/common/customLabel/CustomLabel.jsx';
import { sizes } from 'src/styles/Media.styled';
import { ReportUsageContext } from 'src/pages/reportUsage';
import { useTranslation } from 'react-i18next';
import StepperAddBlue from 'src/assets/images/StepperAddBlue.svg';
import CloseIcon from 'src/assets/images/CloseProduct.svg';
import Loader from 'src/common/components/Loader.jsx';
import CountBadge from '../countBadge/CountBadge';
import { useSelector } from 'react-redux';
import CheckMark from 'src/assets/images/CheckMark.svg';
import Nontissue from 'src/assets/images/NonTissueItemIcon.svg';
import Tissue from 'src/assets/images/TissueIcon.svg';
import { getCartCount } from 'src/utils/util';
import { DARK_NAVY_FONT_COLOR } from 'src/styles/fonts/MCFontConstants';
import { useEffectWithErrorLogging } from 'src/utils/useEffectWithErrorLogging';
import { FONT_FAMILY_BOLD, FONT_FAMILY_REGULAR, SECONDARY_FONT_COLOR, SEMANTIC_TEXT_XSMALL_PX, STATUS_TEXT,X_SMALL_TEXT_PX } from '../../../styles/fonts/MCFontConstants';

const SearchDropdown = ({
    data,
    titleField,
    descField,
    onSelectItem,
    padding,
    productSearchDropdown,
    isFromConsignmentADD,
    isFromEquity,
    onClose,
    cartProducts,  
    isFromSurgeryTemplate,
    isFromEquityTemplate,
    isConsignmentBilling,
    cartData = [],
    checkLotSerial = false,
}) => {
    const { t } = useTranslation();
    const containerRef = useRef(null);
    const createdCloseNodeRef = useRef(null);
    const [closeDropdownTarget, setCloseDropdownTarget] = useState(null);
    const loadingStatusArray = useSelector(
        (state) => state.validateProductsReducer?.loadingStatusArray || []
    );
    const consignmentAddLoadingStatusArray = useSelector(
        (state) => state.consignmentAddValidateProductsReducer?.loadingStatusArray || []
    );
    const equityLoadingStatusArray = useSelector(
        (state) => state.equityValidateProductsReducer?.loadingStatusArray || []
    );
    const equitypricingStatusArray = useSelector(
        (state) => state.getPricesForItemsReducer?.pricingStatusArray || []
    );

    const { validatedProducts } = useSelector((state) => ({
        validatedProducts: (isFromEquity || isFromEquityTemplate)
            ? state.equityValidateProductsReducer?.validatedProducts
            : isFromConsignmentADD
                ? state.consignmentAddValidateProductsReducer?.validatedProducts
                : state.validateProductsReducer?.validatedProducts,
    }));

    const productsInCart =  (isFromSurgeryTemplate || isFromEquityTemplate) ? cartData : cartProducts;
    const hasLotSerial = isConsignmentBilling || checkLotSerial;
    const cartCount = getCartCount(productsInCart, data,hasLotSerial, isFromSurgeryTemplate || isFromEquityTemplate);
        
    const isProductLoading = 
    (isFromEquity || isFromEquityTemplate)?
    ((Array.isArray(equityLoadingStatusArray) &&
        equityLoadingStatusArray.some(
              (item) =>
                  item?.loading &&
                  item?.cfn === (data?.cfn || data?.productCFN)
          )) ||
        (Array.isArray(equitypricingStatusArray) &&
        equitypricingStatusArray.some(
              (item) =>
                  item?.loading &&
                  item?.cfn === (data?.cfn || data?.productCFN)
          )))
    : isFromConsignmentADD?
     (Array.isArray(consignmentAddLoadingStatusArray)
        ? consignmentAddLoadingStatusArray.some(
              (item) =>
                  item?.loading &&
                  item?.cfn === (data?.cfn || data?.productCFN) &&
                  (item?.salesOrg === data?.salesOrg)
          ) : false)
    : Array.isArray(loadingStatusArray)
        ? loadingStatusArray.some(
              (item) =>
                  item?.loading &&
                  item?.cfn === (data?.cfn || data?.productCFN) &&
                  (item?.salesOrg === data?.salesOrg)
          )
        : false;

    const isEquityPricingLoading = (isFromEquity || isFromEquityTemplate) &&
        Array.isArray(equitypricingStatusArray) &&
        equitypricingStatusArray.some(
            (item) => item?.loading && item?.cfn === (data?.cfn || data?.productCFN)
        );

    const matchedValidatedProduct = Array.isArray(validatedProducts)
        ? validatedProducts.find(
              (product) => ((product?.cfn || product?.productCFN) === (data?.cfn || data?.productCFN)) &&
                            (product?.salesOrg === data?.salesOrg)
          )
        : null;
    const productAdded = Boolean(matchedValidatedProduct?.productAdded);
    const [isAddPending, setIsAddPending] = useState(false);
    const [showCheckMark, setShowCheckMark] = useState(false);
    const prevEquityPricingLoadingRef = useRef(false);
    const wasProductAddedRef = useRef(false);
    const checkMarkTimerRef = useRef(null);

    // Remember if productAdded was true at any point during the pricing cycle
    if (productAdded) {
        wasProductAddedRef.current = true;
    }

    // To show consistent loader experience while product is being validated and added to cart,
    //  especially for equity where pricing happens immediately after validation
    useEffectWithErrorLogging(() => {
        if (isProductLoading) {
            setIsAddPending(true);
            return;
        }

        if (!isAddPending) return;

        if (matchedValidatedProduct || productAdded) {
            setIsAddPending(false);
            return;
        }

        const timer = setTimeout(() => {
            setIsAddPending(false);
        }, 300);

        return () => clearTimeout(timer);
    }, [
        isAddPending,
        isProductLoading,
        matchedValidatedProduct,
        equitypricingStatusArray,
        productAdded,
    ]);

    // To show checkmark after equity pricing finishes if product was added 
    useEffectWithErrorLogging(() => {
        const wasLoading = prevEquityPricingLoadingRef.current;
        prevEquityPricingLoadingRef.current = isEquityPricingLoading;

        if (wasLoading && !isEquityPricingLoading && wasProductAddedRef.current) {
            wasProductAddedRef.current = false;
            setShowCheckMark(true);
            const timer = setTimeout(() => {
                setShowCheckMark(false);
            }, 2000);
            return () => clearTimeout(timer);
        }
    }, [isEquityPricingLoading]);

    // To show checkmark other than equity flow
    useEffectWithErrorLogging(() => {
        if (isFromEquity) {
            // For equity: pricing transition effect handles the case when pricing runs after
            // validation. But if pricing finishes before validation (race condition) or is
            // never triggered (product already priced), the pricing transition never fires
            // with wasProductAddedRef=true. So show checkmark here when productAdded=true
            // and pricing is not currently in progress.
            if (productAdded && !isEquityPricingLoading) {
                setShowCheckMark(true);
                clearTimeout(checkMarkTimerRef.current);
                checkMarkTimerRef.current = setTimeout(() => {
                    setShowCheckMark(false);
                }, 2000);
            }
            return;
        }

        if (!productAdded) {
            // SurgeryTemplate dispatches clearValidatedProducts() immediately after add,
            // resetting productAdded → false. Skip hiding checkmark for surgery template —
            // the ref-based timer started below will hide it after 2s on its own.
            if (!isFromSurgeryTemplate) {
                showCheckMark && setShowCheckMark(false);
            }
        } else {
            setShowCheckMark(true);
            if (isFromSurgeryTemplate) {
                // Ref-based timer: cleanup (return fn) is NOT used here, so when the
                // effect re-runs with productAdded=false it won't cancel this timer.
                clearTimeout(checkMarkTimerRef.current);
                checkMarkTimerRef.current = setTimeout(() => {
                    setShowCheckMark(false);
                }, 2000);
            } else {
                const timer = setTimeout(() => {
                    setShowCheckMark(false);
                }, 2000);
                return () => clearTimeout(timer);
            }
        }
    }, [productAdded]);

    const shouldShowLoader = isProductLoading || isAddPending;
    const usageDetails = useContext(ReportUsageContext)?.usageDetails;
    const bundleHeadersList = usageDetails?.bundleHeaders
        ? usageDetails.bundleHeaders?.split(',')
        : [];

    useEffectWithErrorLogging(() => {
        const optionNode =
            containerRef.current?.closest('li.MuiAutocomplete-option');
        if (!optionNode) {
            return;
        }

        const listNode = optionNode.parentElement;
        const paperNode = optionNode.closest('.MuiAutocomplete-paper');
        const isFirstOption = !optionNode.previousElementSibling;

        if (!listNode || !paperNode || !isFirstOption) {
            return;
        }

        const existingCloseOption = paperNode.querySelector(
            '.close-dropdown-option'
        );
        if (existingCloseOption) {
            setCloseDropdownTarget(existingCloseOption);
            return;
        }

        const closeNode = document.createElement('div');
        closeNode.className = 'close-dropdown-option';
        closeNode.style.zIndex = '6';
        closeNode.style.margin = '0';
        closeNode.style.background = '#f3efef';
        closeNode.style.display = 'block';

        paperNode.insertBefore(closeNode, listNode);
        createdCloseNodeRef.current = closeNode;
        setCloseDropdownTarget(closeNode);

        return () => {
            if (createdCloseNodeRef.current) {
                createdCloseNodeRef.current.remove();
                createdCloseNodeRef.current = null;
                setCloseDropdownTarget(null);
            }
        };
    }, [data]);

    const handleCloseDropdown = (event) => {
        event.stopPropagation();
        onClose(); 
    };

    const isDisabled = isProductLoading || productAdded;

    // Set aria-disabled on parent <li> so MUI Autocomplete natively skips it
    useEffectWithErrorLogging(() => {
        const optionNode = containerRef.current?.closest('li.MuiAutocomplete-option');
        if (optionNode) {
            optionNode.setAttribute('aria-disabled', isDisabled); 
            optionNode.style.opacity = '1'; 
        }
    }, [isDisabled]);

    return (
        <AutoListContainer
            ref={containerRef}
            id={`auto-list-item-${data?.id || data?.cfn || ''}`}
            onClick={() => {
                if (isDisabled) return;
                onSelectItem && onSelectItem(data);
            }}
            padding={padding}
        >
            {closeDropdownTarget &&
                createPortal(
                    <div
                        onClick={handleCloseDropdown}
                        style={{
                            borderBottom: '1px solid #dee2e6',
                            padding: '8px 12px',
                            width: '100%',
                            boxSizing: 'border-box',
                            cursor: 'pointer',
                            display: 'flex',
                            justifyContent: 'space-between',
                            background: '#F5F5F5',
                            alignItems: 'center',
                            opacity: 1,
                        }}
                    >
                        <CloseDropdownText color={`${SECONDARY_FONT_COLOR}`} fontSize={'12px'}>
                            {t('common.closeDropdown', {
                                defaultValue: 'Close dropdown',
                            })}
                        </CloseDropdownText>
                        <img src={CloseIcon} alt="close" style={{ width: '12px', height: '12px', marginRight:'12px' }} />
                    </div>,
                    closeDropdownTarget
                )}

            <AutoCompleteTemplateHeader>
                {productSearchDropdown ? (
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <div className="setNumber">
                            {data.cfn} &nbsp;&nbsp;
                            <b> | </b> &nbsp;&nbsp;
                            {data.brand}
                        </div>
                        <AutoCompleteDescription>
                            {data && data[`${descField}`]
                                ? data[`${descField}`]
                                : data.description}
                        </AutoCompleteDescription>
                    </div>
                ) : (
                    <CustomLabel
                        label={
                            data && data[`${titleField}`]
                                ? data[`${titleField}`]
                                : data.cfn
                        }
                        fWeight={FONT_FAMILY_BOLD}
                        color={DARK_NAVY_FONT_COLOR}
                        fSize={X_SMALL_TEXT_PX}
                    />
                )}

                <MultiDivRowView>
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            padding: '0 20px',
                            gap: '10px',
                        }}
                    >
                        {
                        ((bundleHeadersList?.includes(data?.cfn?.toUpperCase()))
                        || (data.materialType === 'ZKMT')) ? (
                            <BoxView
                                bgColor="#F2EFFF"
                                bgBorder={'0.5px solid #C0B2FF'}
                                scrHeight={'20px'}
                                scrWidth={'58px'}
                                boxpadding={'1px 4px 0'}
                            >
                                <CustomText color={STATUS_TEXT}>
                                    {t('equity.common.bundleItems.bundle')}
                                </CustomText>
                            </BoxView>
                        ) : data?.salesOrg === 'S002' ? (
                            <img src={Nontissue} alt={'Non-Tissue'}/>
                        ) : (
                            data?.salesOrg === 'S030' && (
                            <img src={Tissue} alt={'Tissue'}/>   
                            )
                        )}

                        {data && data?.uom !== '' && (
                            <BoxView
                                bgColor="#EBEBEB"
                                bgBorder={'none'}
                                bRadius={'9px'}
                                scrWidth={'fit-content'}
                                minWidth={'30px'}
                                scrHeight={'20px'}
                                ml={
                                    window.innerWidth > sizes.mobileDevice
                                        ? '0px'
                                        : '2px'
                                }
                                mr={
                                    window.innerWidth > sizes.mobileDevice
                                        ? '20px'
                                        : '2px'
                                }
                            >
                                <CustomText
                                    color={DARK_NAVY_FONT_COLOR}
                                    fontFamily={FONT_FAMILY_REGULAR}
                                    fontSize={SEMANTIC_TEXT_XSMALL_PX}
                                    paddingTop={'1px'}
                                >
                                    {data.isSet ? t('common.setLabel') : data.uom}
                                </CustomText>
                            </BoxView>
                        )}
                    </div>
                    <AddedCartButton hasBadge={!shouldShowLoader && !showCheckMark}>
                        {shouldShowLoader ? 
                            <Spinner />
                            : 
                        showCheckMark?
                          <img
                                src={CheckMark}
                                width="30px"
                                height="30px"
                            />
                         : (
                            <>
                                <AddCartIcon mobilePath={StepperAddBlue} />
                                <CountBadge count={cartCount} id={`count-badge-${data?.cfn}`} />
                            </>
                        )}
                    </AddedCartButton>
                </MultiDivRowView>
            </AutoCompleteTemplateHeader>
        </AutoListContainer>
    );
};

export default SearchDropdown;
